import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Agent } from '../types';
import { useAgentsApi } from '../apis/agents-apis';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from "../components/ui/Toast";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Badge } from "../components/ui/Badge";
import { Spinner } from "../components/ui/Spinner";
import { 
  ArrowLeft, 
  Edit3, 
  Star, 
  Settings, 
  Brain, 
  Tag,
  Shield,
  Eye,
  PenTool,
  Calendar
} from 'lucide-react';

export const ViewAgentPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { user, isAdmin } = useAuth();
  const agentsApi = useAgentsApi();
  
  const [agent, setAgent] = useState<Agent | null>(null);
  const [loading, setLoading] = useState(true);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  useEffect(() => {
    const fetchAgent = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const agentData = await agentsApi.getAgent(id);
        setAgent(agentData);
      } catch (error) {
        console.error('Error fetching agent:', error);
        addToast({
          title: 'Error',
          description: 'Failed to load agent. Please try again.',
          variant: 'destructive',
        });
        navigate('/studio/agents');
      } finally {
        setLoading(false);
      }
    };

    fetchAgent();
  }, [id]);

  const handleCopyToClipboard = async (text: string, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
      addToast({
        title: "Copied",
        description: `${fieldName} copied to clipboard`,
        variant: "default",
      });
    } catch (error) {
      addToast({
        title: "Error",
        description: "Failed to copy to clipboard",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <Spinner className="w-8 h-8 text-purple-500" />
        <p className="text-gray-600">Loading agent details...</p>
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="container mx-auto p-8">
        <div className="text-center py-12">
          <div className="bg-gradient-to-br from-red-100 to-red-200 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <Eye className="h-8 w-8 text-red-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Agent Not Found</h3>
          <p className="text-gray-600 mb-4">The requested agent could not be found.</p>
          <Button 
            variant="outline" 
            onClick={() => navigate('/studio/agents')}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Agents
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      {/* Hero Header Section */}
      <div className="relative mb-8 overflow-hidden rounded-3xl">
        {/* Multi-layer gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-600 via-indigo-600 to-purple-800"></div>
        <div className="absolute inset-0 bg-gradient-to-tr from-purple-500/30 via-transparent to-indigo-500/30"></div>
        
        {/* Animated background elements */}
        <div className="absolute top-10 left-10 w-72 h-72 bg-white/5 rounded-full blur-xl animate-pulse"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-indigo-400/10 rounded-full blur-2xl animate-pulse"></div>
        
        {/* Glassmorphism overlay */}
        <div className="absolute inset-0 backdrop-blur-sm bg-gradient-to-r from-white/10 to-transparent"></div>
        
        {/* Content */}
        <div className="relative z-10 px-8 py-12">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            {/* Back Button */}
            <div className="absolute top-6 left-6">
              <Button
                variant="ghost"
                onClick={() => navigate('/studio/agents')}
                className="text-white hover:bg-white/20 border border-white/20 hover:border-white/40"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Agents
              </Button>
            </div>

            <div className="flex-1 min-w-0 pt-12">
              <div className="flex flex-col sm:flex-row sm:items-center gap-6 mb-6">
                {/* Agent Icon */}
                <div className="bg-white/20 backdrop-blur-sm p-4 rounded-3xl shadow-lg border border-white/30">
                  <Brain className="h-12 w-12 text-white" />
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <h1 className="text-4xl font-bold text-white tracking-tight drop-shadow-lg">
                      {agent.agentName}
                    </h1>
                    {agent.isCore && (
                      <Badge className="bg-yellow-500/20 text-yellow-100 border border-yellow-400/30">
                        <Star className="w-3 h-3 mr-1" />
                        Core
                      </Badge>
                    )}
                  </div>
                  
                  <p className="text-purple-100 text-lg font-medium max-w-2xl leading-relaxed mb-4">
                    {agent.agentDescription || 'No description provided.'}
                  </p>

                  {/* Stats */}
                  <div className="flex items-center gap-6 flex-wrap">
                    <div className="bg-white/10 backdrop-blur-sm px-4 py-3 rounded-2xl border border-white/20">
                      <div className="flex items-center gap-3">
                        <Settings className="h-5 w-5 text-cyan-300" />
                        <div>
                          <div className="text-white text-sm font-medium">Tools</div>
                          <div className="text-2xl font-bold text-cyan-200">
                            {agent.agentSelectedTools?.length || 0}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white/10 backdrop-blur-sm px-4 py-3 rounded-2xl border border-white/20">
                      <div className="flex items-center gap-3">
                        <Tag className="h-5 w-5 text-green-300" />
                        <div>
                          <div className="text-white text-sm font-medium">Tags</div>
                          <div className="text-2xl font-bold text-green-200">
                            {agent.agentTags?.length || 0}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Action Button */}
            <div className="flex items-center gap-3">
              <Button
                onClick={() => navigate(`/studio/agents/edit/${agent.agentId}`)}
                className="group relative bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white border border-white/30 hover:border-white/50 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 px-8 py-4 rounded-2xl font-semibold text-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="bg-white/20 p-2 rounded-xl group-hover:bg-white/30 transition-colors">
                    <Edit3 className="h-5 w-5" />
                  </div>
                  <span>Edit Agent</span>
                </div>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - Agent Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Agent Prompt */}
          <Card className="border-purple-200/50 hover:border-purple-300 transition-colors">
            <CardHeader className="bg-gradient-to-r from-purple-50 to-indigo-50 border-b border-purple-200/50">
              <CardTitle className="flex items-center gap-2 text-purple-900">
                <Brain className="h-5 w-5 text-purple-600" />
                Agent Prompt & Instructions
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="bg-gray-50 rounded-xl p-4 font-mono text-sm whitespace-pre-wrap border border-gray-200 max-h-64 overflow-y-auto">
                {agent.agentPrompt || 'No prompt defined for this agent.'}
              </div>
            </CardContent>
          </Card>

          {/* Tools Showcase */}
          <Card className="border-purple-200/50 hover:border-purple-300 transition-colors">
            <CardHeader className="bg-gradient-to-r from-purple-50 to-indigo-50 border-b border-purple-200/50">
              <CardTitle className="flex items-center gap-2 text-purple-900">
                <Settings className="h-5 w-5 text-purple-600" />
                Active Tools ({agent.agentSelectedTools?.length || 0})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {(!agent.agentSelectedTools || agent.agentSelectedTools.length === 0) ? (
                <div className="text-center py-12 text-gray-500">
                  <Settings className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-medium">No tools configured</p>
                  <p className="text-sm">This agent doesn't have any tools attached yet.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {agent.agentSelectedTools.map((tool) => (
                    <Card key={tool.toolId} className="group hover:shadow-md transition-all duration-200 border-gray-200 hover:border-purple-300">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className="bg-gradient-to-br from-emerald-100 to-teal-200 p-2 rounded-lg flex-shrink-0">
                            <Settings className="h-4 w-4 text-emerald-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-gray-900 group-hover:text-purple-600 transition-colors">
                              {tool.toolName}
                            </h4>
                            <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                              {tool.toolDescription || 'No description available.'}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Metadata & Tags */}
        <div className="space-y-6">
          {/* Tags Display */}
          {agent.agentTags && agent.agentTags.length > 0 && (
            <Card className="border-purple-200/50 hover:border-purple-300 transition-colors">
              <CardHeader className="bg-gradient-to-r from-purple-50 to-indigo-50 border-b border-purple-200/50">
                <CardTitle className="flex items-center gap-2 text-purple-900">
                  <Tag className="h-5 w-5 text-purple-600" />
                  Tags & Categories
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="flex flex-wrap gap-2">
                  {agent.agentTags.map((tag, index) => (
                    <Badge 
                      key={index}
                      variant="outline"
                      className="bg-purple-100 text-purple-700 hover:bg-purple-200 transition-colors border-purple-200"
                    >
                      {tag}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Access Control */}
          <Card className="border-purple-200/50 hover:border-purple-300 transition-colors">
            <CardHeader className="bg-gradient-to-r from-purple-50 to-indigo-50 border-b border-purple-200/50">
              <CardTitle className="flex items-center gap-2 text-purple-900">
                <Shield className="h-5 w-5 text-purple-600" />
                Access Control
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              {agent.isCore ? (
                <div className="flex items-center space-x-3 text-gray-700">
                  <div className="bg-yellow-100 p-2 rounded-lg">
                    <Star className="h-5 w-5 text-yellow-600" />
                  </div>
                  <div>
                    <p className="font-medium">Core Agent</p>
                    <p className="text-sm text-gray-600">Administrator managed only</p>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between py-2">
                    <div className="flex items-center gap-2">
                      <Eye className="h-4 w-4 text-gray-500" />
                      <span className="text-sm font-medium">View Access</span>
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {agent.accessControl?.viewAccessType || 'Owner Only'}
                    </Badge>
                  </div>
                  
                  <div className="flex items-center justify-between py-2">
                    <div className="flex items-center gap-2">
                      <PenTool className="h-4 w-4 text-gray-500" />
                      <span className="text-sm font-medium">Edit Access</span>
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {agent.accessControl?.editAccessType || 'Owner Only'}
                    </Badge>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Metadata */}
          <Card className="border-purple-200/50 hover:border-purple-300 transition-colors">
            <CardHeader className="bg-gradient-to-r from-purple-50 to-indigo-50 border-b border-purple-200/50">
              <CardTitle className="flex items-center gap-2 text-purple-900">
                <Calendar className="h-5 w-5 text-purple-600" />
                Metadata
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-3 text-sm">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Agent ID</span>
                <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">{agent.agentId}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Tools Count</span>
                <span>{agent.agentSelectedTools?.length || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Tags Count</span>
                <span>{agent.agentTags?.length || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Core Agent</span>
                <span>{agent.isCore ? 'Yes' : 'No'}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ViewAgentPage;